'use client';

import { ReactTooltip as Tooltip } from '@paction-2.0/ui/client';

import styles from './breedHealthConsiderations.module.scss';

type HealthTest = {
  screening: string | null;
  considerations: string | null;
  tooltip: string | null;
};

type ParentHealthTesting = {
  breedName: string | null;
  healthTests: HealthTest[];
};

type BreedHealthConsiderationsProps = {
  parentHealthTesting: ParentHealthTesting[];
};

export function BreedHealthConsiderations({
  parentHealthTesting
}: BreedHealthConsiderationsProps) {
  if (parentHealthTesting.length === 0) {
    return null;
  }

  return (
    // id anchors deep links from the puppy search pages' compact
    // health-testing extract (BreedHealthTesting).
    <section id="health-testing" className={styles.healthConsiderations}>
      <h2 className={styles.title}>What to ask your breeder?</h2>
      <div className={styles.description}>
        It&apos;s important to ensure your breeder conducts thorough health
        screenings on the two dogs that were bred for the litter. Reputable
        breeders should perform genetic laboratory testing and also orthopedic
        evaluations. There&apos;s no way to guarantee every puppy in every
        litter is 100% free from health problems, but responsible breeding
        practices greatly reduce the risk.
        <p>
          Here&apos;s a short summary of what you should be asking your breeder:
        </p>
      </div>
      {parentHealthTesting.map((testing, tableIndex) => (
        <div key={tableIndex} className={styles.tableContainer}>
          <h3 className={styles.heading}>{testing.breedName} Health Testing</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.tableHeader}>Screening</th>
                <th className={styles.tableHeader}>Considerations</th>
              </tr>
            </thead>
            <tbody>
              {testing.healthTests.map((item, itemIndex) => {
                const tooltipId = `health-tooltip-${tableIndex}-${itemIndex}`;
                return (
                  <tr key={itemIndex}>
                    <td
                      data-tooltip-id={item.tooltip ? tooltipId : undefined}
                      data-tooltip-content={item.tooltip || undefined}
                      className={item.tooltip ? styles.hasTooltip : undefined}
                    >
                      {item.screening}
                      {item.tooltip && (
                        <Tooltip
                          id={tooltipId}
                          place="top"
                          className={styles.tooltip}
                          style={{
                            backgroundColor: 'var(--color-brand-primary-500)',
                            color: '#fff',
                            zIndex: 100000,
                            maxWidth: '20rem',
                            fontSize: 'var(--font-size-sm)',
                            padding: 'var(--space-sm) var(--space-md)',
                            borderRadius: 'var(--radius-md)'
                          }}
                        />
                      )}
                    </td>
                    <td>{item.considerations}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
